/**
 * Small labeled-field wrapper shared by every section-card edit
 * dialog. Renders a `<label>` row with the label text + required
 * asterisk + optional hint, then slots the actual input as
 * children so the caller picks the right control (Input, Select,
 * textarea, etc.).
 */
import * as React from "react";

export interface SectionFieldProps {
  label: React.ReactNode;
  required?: boolean;
  /** Trailing `(…)` text after the label — e.g. `"optional"`, `"for invoice delivery"`. */
  optional?: string;
  /** Below-input help text. */
  hint?: React.ReactNode;
  /**
   * Inline validation error. When present, replaces the `hint` slot with a
   * red message so the field's problem is visible without a top-of-dialog
   * alert. Nulls / undefined = no error shown.
   */
  error?: string | null;
  /** The input control. */
  children: React.ReactNode;
}

export function SectionField({ label, required, optional, hint, error, children }: SectionFieldProps) {
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className="text-xs font-semibold text-ink-2">
        {label}
        {required ? <span className="text-bad ml-0.5">*</span> : null}
        {optional ? <span className="text-muted font-medium ml-1">({optional})</span> : null}
      </span>
      {children}
      {error ? (
        <span className="text-xs text-bad font-medium leading-snug" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="text-xs text-muted leading-snug">{hint}</span>
      ) : null}
    </label>
  );
}
